import {
  CandlestickChartOutlined,
  GppMaybeOutlined,
  HelpOutlineOutlined,
  HomeOutlined,
  InsightsOutlined,
  PlaylistPlayOutlined,
  RadarOutlined,
  ReorderOutlined,
  ScienceOutlined,
  SettingsOutlined,
  ShowChartOutlined,
  TableChartOutlined,
  GridView,
  Engineering,
  DonutLarge,
  TuneOutlined,
} from '@mui/icons-material';
import IconMaximize from '@tabler/icons-react/dist/esm/icons/IconMaximize';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import type { ReactNode } from 'react';

import { Correlation, Network, Plants, ServerError, SmoothLineChart } from '../icons';
import type { DistributiveOmit } from '../../type.utils.ts';
import isNil from 'lodash/fp/isNil';
import { MARKET_REGIME } from 'Permissions.ts';
import { SvgIcon } from '@mui/joy';
import { useAuth } from 'components/management/NewAuthProvider';

const assetScreenerLink = '/app/market/asset-screener';
const quantLink = '/app/market/clusters';
const marketRegimesLink = '/app/market/regime';
const assetsLink = '/app/market/assets';
const correlationsLink = '/app/market/correlations';
const portfolioDashboardLink = '/app/portfolio/dashboard';
const portfolioPnLLink = '/app/portfolio/profit-loss';
const portfolioPnLSubFundLink = '/app/portfolio/subfund-profit-loss';
const portfolioPositionsLink = '/app/portfolio/positions';
export const operationReportLink = '/app/operations/report';
const stressTestLink = '/app/copilot/stress-test/spot';
const strategiesLink = '/app/copilot/strategies';
const operationOrdersLink = '/app/operations/orders';
export const portfolioRiskLink = '/app/portfolio/risk';
const portfolioOptimizerLink = '/app/copilot/portfolio-optimizer';
export const portfolioOptimizerLinkNewPortfolioOptimization =
  '/app/copilot/portfolio-optimizer/optimizations/new-portfolio-optimization';
export const portfolioOptimizerLinkNewStrategyOptimization =
  '/app/copilot/portfolio-optimizer/optimizations/new-strategy-optimization';
export const portfolioOptimizerLinkNewYieldOptimization =
  '/app/copilot/portfolio-optimizer/optimizations/new-yield-optimization';
const portfolioBuilderLink = '/app/copilot/portfolio-builder';
const portfolioLabLink = '/app/copilot/lab';
const operationTransactionsLink = '/app/operations/transactions';
const operationInvestmentsLink = '/app/operations/investments';
const operationTradingActivityLink = '/app/operations/trading-activity';
const reconciliationLink = '/app/operations/reconciliation';
const customDashboardLink = '/app/portfolio/custom-dashboard';
export const notificationLink = '/app/copilot/notifications';
export const playgroundLink = '/app/playground';
export const managementLink = '/app/management';
export const termsAndPoliciesLink = '/app/terms-and-policies';

export type SingleMenuItem = {
  type: 'item';
  title: ReactNode;
  icon: ReactNode;
  link: string;
  isActive?: (activePath: string) => boolean;
  newTab?: boolean;
  new?: boolean;
} & ({ adminOnly?: true; groups?: never[] } | { groups: string[]; adminOnly?: false });

export interface GroupMenuItem {
  type: 'group';
  title: string;
  icon: ReactNode;
  items: DistributiveOmit<SingleMenuItem, 'type'>[];
}

export type MenuItem = SingleMenuItem | GroupMenuItem;
const joyFontSize = 'xl2';
const muiFontSize = 'medium';

const pmsMenu = [
  {
    type: 'group',
    title: 'Portfolio',
    icon: <AccountBalanceWalletOutlinedIcon fontSize={muiFontSize} />,
    items: [
      {
        title: 'Overview',
        icon: <HomeOutlined fontSize={muiFontSize} />,
        link: portfolioDashboardLink,
      },
      {
        title: 'Positions',
        icon: <DonutLarge fontSize={muiFontSize} />,
        link: portfolioPositionsLink,
      },
      {
        title: 'P&L',
        icon: <ShowChartOutlined fontSize={muiFontSize} />,
        link: portfolioPnLLink,
      },
      {
        title: 'Sub-fund P&L',
        icon: <ShowChartOutlined fontSize={muiFontSize} />,
        link: portfolioPnLSubFundLink,
      },
      {
        title: 'Risk',
        icon: <GppMaybeOutlined fontSize={muiFontSize} />,
        link: portfolioRiskLink,
      },
      {
        title: 'My dashboard',
        icon: <GridView fontSize={muiFontSize} />,
        link: customDashboardLink,
      },
    ],
  },
  {
    type: 'group',
    title: 'Operations',
    icon: <CandlestickChartOutlined fontSize={muiFontSize} />,
    items: [
      {
        title: 'Report',
        icon: <GridView fontSize={muiFontSize} />,
        link: operationReportLink,
      },
      {
        title: 'Trading activity',
        icon: <TableChartOutlined fontSize={muiFontSize} />,
        link: operationTradingActivityLink,
      },
      {
        title: 'Orders',
        icon: <PlaylistPlayOutlined fontSize={muiFontSize} />,
        link: operationOrdersLink,
      },
      {
        title: 'Transactions',
        icon: <ReorderOutlined fontSize={muiFontSize} />,
        link: operationTransactionsLink,
      },
      {
        title: 'Venture investments',
        icon: <Plants fontSize={joyFontSize} />,
        link: operationInvestmentsLink,
      },
      {
        title: 'Reconciliation',
        icon: <ServerError fontSize={joyFontSize} />,
        link: reconciliationLink,
      },
    ],
  },
] satisfies MenuItem[];

const portfolioLabMenu = [
  {
    type: 'group',
    title: 'Copilot',
    icon: <RadarOutlined fontSize={muiFontSize} />,
    items: [
      {
        title: 'Lab',
        icon: <ScienceOutlined fontSize={muiFontSize} />,
        link: portfolioLabLink,
      },
      {
        title: 'Stress test',
        icon: <SmoothLineChart fontSize={joyFontSize} />,
        link: stressTestLink,
      },
      {
        title: 'Portfolio optimizer',
        icon: <TuneOutlined fontSize={muiFontSize} />,
        link: portfolioOptimizerLink,
        isActive: (activePath: string): boolean => activePath.startsWith(portfolioOptimizerLink),
      },
      {
        title: 'Portfolio builder',
        icon: <Engineering fontSize={muiFontSize} />,
        link: portfolioBuilderLink,
      },
      {
        title: 'Strategies',
        icon: <Network fontSize={joyFontSize} />,
        link: strategiesLink,
        adminOnly: true,
      },
    ],
  },
  {
    type: 'group',
    title: 'Research',
    icon: <CandlestickChartOutlined fontSize={muiFontSize} />,
    items: [
      {
        title: 'Asset screener',
        icon: <SvgIcon component={IconMaximize} fontSize={joyFontSize} />,
        link: assetScreenerLink,
      },
      {
        title: 'Quant insights',
        icon: <InsightsOutlined fontSize={muiFontSize} />,
        link: quantLink,
        adminOnly: true,
        isActive: (activePath: string): boolean =>
          activePath.startsWith(quantLink) || activePath.startsWith(assetsLink),
      },
      {
        title: 'Market regime',
        icon: <InsightsOutlined fontSize={muiFontSize} />,
        link: marketRegimesLink,
        groups: [MARKET_REGIME],
      },
      {
        title: 'Asset correlations',
        icon: <Correlation fontSize={joyFontSize} />,
        link: correlationsLink,
      },
    ],
  },
] satisfies MenuItem[];

export const useCreateMenu = (): MenuItem[] => {
  const { hasAppAccess } = useAuth();
  if (hasAppAccess('pms')) {
    return pmsMenu;
  }

  if (hasAppAccess('portfolioLab')) {
    return portfolioLabMenu;
  }

  return [];
};

export const footerMenu = [
  {
    type: 'item',
    title: 'Control center',
    icon: <SettingsOutlined fontSize={muiFontSize} />,
    link: managementLink,
  },
  {
    type: 'item',
    title: 'Terms and policies',
    icon: <HelpOutlineOutlined fontSize={muiFontSize} />,
    link: termsAndPoliciesLink,
  },
] satisfies MenuItem[];

export const isActive = (item: Pick<SingleMenuItem, 'isActive' | 'link'>, activePath: string): boolean => {
  return item.isActive ? item.isActive(activePath) : activePath.startsWith(item.link);
};

export const shouldShowMenuItem = (
  item: MenuItem,
  isAdmin: boolean,
  hasAccess: (group: string) => boolean
): boolean => {
  if (item.type === 'item') {
    if (isAdmin) {
      return true;
    }

    if (item.adminOnly) {
      return isAdmin;
    }

    if (isNil(item.groups) || item.groups.length === 0) {
      return true;
    }

    return item.groups.some((group) => hasAccess(group));
  }

  return item.items.some((subitem) =>
    shouldShowMenuItem(
      {
        ...subitem,
        type: 'item',
      },
      isAdmin,
      hasAccess
    )
  );
};
